import { useState, useRef, useCallback } from 'react';

interface UseCountUpOptions {
    number: number;   // 目标数字
    time: number;     // 动画时长 ms
    decimals?: number; // 小数位数，默认自动识别
}

export function useCountUp({ number, time, decimals }: UseCountUpOptions) {
    const rafRef = useRef<number | null>(null);
    const numberRef = useRef(number);

    // 自动识别小数位长度
    const decimalPlaces =
        decimals !== undefined
            ? decimals
            : number.toString().includes('.')
                ? number.toString().split('.')[1].length
                : 0;

    // 格式化数字：保留小数 + 千分位
    const formatNumber = useCallback((val: number) => {
        return val
            .toFixed(decimalPlaces)
            .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }, [decimalPlaces]);

    const [curNumber, setCurNumber] = useState(formatNumber(0));

    // 动画函数
    const run = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        const startTime = performance.now();
        const target = numberRef.current;

        const step = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / time, 1);
            const value = target * progress;

            setCurNumber(formatNumber(value));

            if (progress < 1) {
                rafRef.current = requestAnimationFrame(step);
            }
        };

        rafRef.current = requestAnimationFrame(step);
    }, [time, formatNumber]);

    // 重置数字
    const reset = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        setCurNumber(formatNumber(0));
    }, [formatNumber]);

    // 更新目标数字
    const updateNumber = useCallback((num: number) => {
        numberRef.current = num;
    }, []);

    return { curNumber, run, reset, updateNumber };
}
